import { getParentCategory, getSegmentName } from "../../src/lib/bestProductsUtils";

import MainLayout from "../../src/components/Layout/MainLayout";
import ProductBestDetail from "../../src/components/Content/Product/ProductBestDetail";
import ProductBestOverview from "../../src/components/Content/Product/ProductBestOverview";
import React from "react";
import SeoHead from "../../src/components/Seo/SeoHead";
import { default as bestProducts } from "../../src/bestProducts.json";
import { default as categoryMapping } from "../../src/categoriesMapping.json";
import environment from "../../src/environment";
import { locales } from "../../i18n";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";

const maxPriceSegment = bestProducts.maxPriceSegment;
const highEndSegment = bestProducts.highEndSegment;

const Page = () => {
  const router = useRouter();
  const { generic } = router.query;
  const { t } = useTranslation("bestBikesCommons");

  const bikeCategoryPaths = Object.keys(categoryMapping).filter(
    (c) =>
      (c != "Fahrräder" && c.startsWith("Fahrräder")) ||
      (c != "E-Bikes" && c.startsWith("E-Bikes") && !c.startsWith("E-Bikes > Teile"))
  );
  const bikeCategoriesByPath = bikeCategoryPaths.reduce((obj, key) => {
    obj[key] = categoryMapping[key];
    return obj;
  }, {});
  const bikeCategoriesByKey = bikeCategoryPaths.reduce((obj, key) => {
    obj[categoryMapping[key]] = key;
    return obj;
  }, {});

  if (!generic || typeof generic !== "string") {
    return null;
  }

  // summary page for bikes
  if (generic === "fahrraeder") {
    return (
      <MainLayout>
        <SeoHead
          title={t("productBestBioBikeMetaTitle")}
          description={t("productBestBioBikeMetaDescription")}
          keywords={t("productBestBioBikeMetaKeywords")}
          ogImage={"/images/The-Cycleverse-OpenGraph-Die-besten.png"}
          ogDescription={t("productBestBioBikeMetaDescription")}
        />
        <ProductBestOverview
          translationsNamespace={"bestBikesBioBikes"}
          items={bestProducts.items["fahrraeder"]}
          flavor="biobike"
          textId="fahrraeder"
        />
      </MainLayout>
    );
  } else if (generic === "e-bikes") {
    return (
      <MainLayout>
        <SeoHead
          title={t("productBestEBikeMetaTitle")}
          description={t("productBestEBikeMetaDescription")}
          keywords={t("productBestEBikeMetaKeywords")}
          ogImage={"/images/The-Cycleverse-OpenGraph-Die-bestenE-Bikes.png"}
          ogDescription={t("productBestEBikeMetaDescription")}
        />
        <ProductBestOverview
          translationsNamespace={"bestBikesEbikes"}
          items={bestProducts.items["e-bikes"]}
          flavor="ebike"
          textId="ebikes"
        />
      </MainLayout>
    );
  }

  if (generic.startsWith(highEndSegment)) {
    const category = generic.split(highEndSegment)[1];
    const categoryPath = bikeCategoriesByKey[category];
    const name = getSegmentName(category);
    const parentCategory = getParentCategory(category);
    if (parentCategory === "fahrraeder") {
      return (
        <MainLayout>
          <SeoHead
            title={t("productBestBioBikeDetailMetaTitleHighEnd", { name })}
            description={t("productBestBioBikeDetailMetaDescriptionHighEnd", { name })}
            keywords={t("productBestBioBikeDetailMetaKeywordsHighEnd", { name })}
            ogImage={"/images/The-Cycleverse-OpenGraph-Die-besten.png"}
            ogDescription={t("productBestBioBikeDetailMetaDescriptionHighEnd", { name })}
          />
          <ProductBestDetail
            translationsNamespace={"bestBikesBioBikes"}
            name={name}
            category={category}
            categoryPath={categoryPath}
            type="highEnd"
          />
        </MainLayout>
      );
    } else if (parentCategory === "e-bikes") {
      return (
        <MainLayout>
          <SeoHead
            title={t("productBestEBikeDetailMetaTitleHighEnd", { name })}
            description={t("productBestEBikeDetailMetaDescriptionHighEnd", { name })}
            keywords={t("productBestEBikeDetailMetaKeywordsHighEnd", { name })}
            ogImage={"/images/The-Cycleverse-OpenGraph-Die-bestenE-Bikes.png"}
            ogDescription={t("productBestEBikeDetailMetaDescriptionHighEnd", { name })}
          />
          <ProductBestDetail
            translationsNamespace={"bestBikesEbikes"}
            name={name}
            category={category}
            categoryPath={categoryPath}
            type="highEnd"
          />
        </MainLayout>
      );
    }
  }

  const genericParts = generic.split(maxPriceSegment);
  const category = genericParts[0];
  const categoryPath = bikeCategoriesByKey[category];
  const maxPrice = genericParts.length > 0 ? parseInt(genericParts[1]) : null;
  const name = getSegmentName(category);
  const parentCategory = getParentCategory(category);

  if (!maxPrice || !categoryPath || !bikeCategoriesByKey[category]) {
    return null;
  }
  if (parentCategory === "fahrraeder") {
    return (
      <MainLayout>
        <SeoHead
          title={t("productBestBioBikeDetailMetaTitleMaxPrice", { name, maxPrice })}
          description={t("productBestBioBikeDetailMetaDescriptionMaxPrice", { name, maxPrice })}
          keywords={t("productBestBioBikeDetailMetaKeywordsMaxPrice", { name, maxPrice })}
          ogImage={"/images/The-Cycleverse-OpenGraph-Die-besten.png"}
          ogDescription={t("productBestBioBikeDetailMetaDescriptionMaxPrice", { name, maxPrice })}
        />
        <ProductBestDetail
          translationsNamespace={"bestBikesBioBikes"}
          name={name}
          category={category}
          categoryPath={categoryPath}
          maxPrice={maxPrice}
          type="maxPrice"
        />
      </MainLayout>
    );
  } else if (parentCategory === "e-bikes") {
    return (
      <MainLayout>
        <SeoHead
          title={t("productBestEBikeDetailMetaTitleMaxPrice", { name, maxPrice })}
          description={t("productBestEBikeDetailMetaDescriptionMaxPrice", { name, maxPrice })}
          keywords={t("productBestEBikeDetailMetaKeywordsMaxPrice", { name, maxPrice })}
          ogImage={"/images/The-Cycleverse-OpenGraph-Die-bestenE-Bikes.png"}
          ogDescription={t("productBestEBikeDetailMetaDescriptionMaxPrice", { name, maxPrice })}
        />
        <ProductBestDetail
          translationsNamespace={"bestBikesEbikes"}
          name={name}
          category={category}
          categoryPath={categoryPath}
          maxPrice={maxPrice}
          type="maxPrice"
        />
      </MainLayout>
    );
  }
};

export async function getStaticPaths() {
  const categories = Object.keys(bestProducts.items);

  const entries = categories
    .map((category) =>
      bestProducts.items[category].flatMap((e) => {
        const categoryKey = e.key;
        const priceKeys = e.priceSegments.map((price) => `${categoryKey}${maxPriceSegment}${price}`);
        const highEndKey = `${highEndSegment}${categoryKey}`;
        return [...priceKeys, highEndKey];
      })
    )
    .flatMap((e) => e);

  const paths = [...categories, ...entries].flatMap((e) => {
    return locales.map((locale) => {
      return {
        params: {
          generic: e,
        },
        locale,
      };
    });
  });

  return {
    paths: environment.features.prerenderBestPrice ? paths : [],
    fallback: "blocking",
  };
}

export async function getStaticProps({ params }) {
  return {
    props: {
      key: params.generic, // force state reset for dynamic route
    },
    revalidate: 60 * 2,
  };
}

export default Page;
